import { COMPETITOR_MAP } from "@/lib/compare/competitors";
import { COMPARE_CONTENT, COMPARE_CONTENT_MAP } from "@/lib/compare/content";
import { ROUNDUPS, ROUNDUP_MAP } from "@/lib/compare/roundups";
import type { CompareContent, CompetitorRecord, RoundupContent } from "@/lib/compare/types";
import { money } from "@/lib/compare/crayo";

export const COMPARE_PATH = "/compare";

export const comparePath = (slug: string): string => `${COMPARE_PATH}/${slug}`;

export type Spoke = { content: CompareContent; competitor: CompetitorRecord };

export const getSpokes = (): Spoke[] =>
  COMPARE_CONTENT.map((content) => ({
    content,
    competitor: COMPETITOR_MAP[content.competitorSlug],
  })).filter((spoke) => Boolean(spoke.competitor));

export const getSpoke = (slug: string): Spoke | undefined => {
  const content = COMPARE_CONTENT_MAP[slug];
  const competitor = content ? COMPETITOR_MAP[content.competitorSlug] : undefined;
  return content && competitor ? { content, competitor } : undefined;
};

export const getRoundup = (slug: string): RoundupContent | undefined => ROUNDUP_MAP[slug];

export const getRoundups = (): RoundupContent[] => ROUNDUPS;

export const spokeSlugForCompetitor = (competitorSlug: string): string | undefined =>
  COMPARE_CONTENT.find((content) => content.competitorSlug === competitorSlug)?.slug;

export const pricesCheckedAt = (competitor: CompetitorRecord): string =>
  [...competitor.plans.map((plan) => plan.checkedAt), competitor.billingTerms.checkedAt]
    .sort()[0] ?? competitor.billingTerms.checkedAt;

export const allCompareSlugs = (): string[] => [
  ...COMPARE_CONTENT.map((content) => content.slug),
  ...ROUNDUPS.map((roundup) => roundup.slug),
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const formatCompareDate = (iso: string): string => {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return `${MONTHS[month - 1]} ${day}, ${year}`;
};

export const formatPlanPrice = (plan: {
  monthly?: number;
  annualPerMonth?: number;
  annualTotal?: number;
  custom?: boolean;
  currency?: "USD" | "CAD";
}): { monthly: string; annual: string } => {
  const amount = (value: number) => (plan.currency === "CAD" ? `CA${money(value)}` : money(value));
  const monthly = plan.custom
    ? "Custom"
    : plan.monthly === undefined
      ? "Not published"
      : plan.monthly === 0
        ? "$0"
        : `${amount(plan.monthly)}/mo`;
  const annual = plan.custom
    ? "Custom"
    : plan.annualPerMonth !== undefined
      ? `${amount(plan.annualPerMonth)}/mo${plan.annualTotal ? ` (${amount(plan.annualTotal)}/yr)` : ""}`
      : plan.annualTotal !== undefined
        ? `${amount(plan.annualTotal)}/yr`
        : plan.monthly === 0
          ? "n/a"
          : "Not offered or not published";
  return { monthly, annual };
};
